<template>
  <EBaseResult
    :title="title"
    :url="url"
    :source="source"
    class="ad-result"
    data-test-id="ad-text-result"
    @title-click="onTitleClick"
  >
    <template #info-start>
      <EAdLabel
        v-if="showAdPillAtStart"
        ref="ad-label"
        :class="{'ad-pill-tooltip__label--regular': !adPillHasBoldFont}"
        :tooltip-side="tooltipSide"
        @report-clicked="onAdReportClick"
      />
    </template>
    <template v-if="brand || showAdPillAtEnd" #info-end>
      <EBrand v-bind="brand" />
      <EAdLabel
        v-if="showAdPillAtEnd"
        ref="ad-label"
        :class="{'ad-pill-tooltip__label--regular': !adPillHasBoldFont}"
        :tooltip-side="tooltipSide"
        @report-clicked="onAdReportClick"
      />
    </template>
    <template #description>
      <EAdQualityFeedbackLink
        ref="ad-report"
        :widget-type="'ad_result'"
        :origin="url"
        class="ad-result__report-link"
      />
      <p
        class="ad-result__description"
        data-test-id="ad-result-description"
      >
        {{ description }}
      </p>
    </template>
    <template #content-end>
      <ESmart v-if="smart" v-bind="smart" />
      <EConsumerRatings v-if="consumerRatings" v-bind="consumerRatings" />
      <EMerchantRating v-if="merchantRating" v-bind="merchantRating" />
    </template>
    <template v-if="action" #columns-end>
      <EAction v-bind="action" />
    </template>
    <EDeepLinks
      v-if="deepLinksCapped.length > 0"
      :links="deepLinksCapped"
      :page-position="pagePosition"
      :rank="rank"
      type="ad"
      data-test-id="ad-result-deep-links"
    />
  </EBaseResult>
</template>

<script>

import { mapGetters } from 'vuex';
import { adRevenueExperiment } from 'common-js/constants/feature-flags';
import { trackClick, TYPE_AD } from '~~/search/common/js/client/analytics/results';
import { AD_DEEP_LINKS_TOTAL } from '~~/search/common/js/constants';
import screen from '~~/common/vue/mixins/screen';
import EAdLabel from '~~/common/vue/components/ad-label';
import EBaseResult, { baseProps } from '../base';
import EDeepLinks from '../add-ons/deep-links';
import EAction from './extensions/action';
import EMerchantRating from './extensions/merchant-rating';
import ESmart from './extensions/smart';
import EBrand from './extensions/brand';
import EConsumerRatings from './extensions/consumer-ratings';
import EAdQualityFeedbackLink from '~~/search/common/vue/components/ad-quality-feedback-link';

export default {
  name: 'EAdResult',
  components: {
    EAdLabel,
    EBaseResult,
    EDeepLinks,
    EAction,
    EMerchantRating,
    EBrand,
    ESmart,
    EConsumerRatings,
    EAdQualityFeedbackLink,
  },
  mixins: [screen],
  props: {
    ...baseProps,
    deepLinks: {
      type: Array,
      default: () => [],
    },
    description: {
      type: String,
      default: '',
    },
    extensions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      adRevenueExperiment: null,
    };
  },
  computed: {
    ...mapGetters('unleash', ['getFeatureToggle']),
    tooltipSide() {
      return this.isDesktopUp ? 'right-center' : 'left-bottom';
    },
    merchantRating() {
      return this.getExtension('merchantRating');
    },
    action() {
      return this.getExtension('action');
    },
    smart() {
      return this.getExtension('smart');
    },
    consumerRatings() {
      return this.getExtension('consumerRatings');
    },
    brand() {
      return this.getExtension('brand');
    },
    deepLinksCapped() {
      return this.deepLinks.slice(0, AD_DEEP_LINKS_TOTAL);
    },
    trackingOptions() {
      return {
        extensions: this.extensions,
        position: this.pagePosition,
        rank: this.rank,
        title: this.title,
        type: TYPE_AD,
        url: this.source,
      };
    },
    adPillHasBoldFont() {
      // since this is also the current state, we want to return true
      // when the experiment is not active at all
      return this.adRevenueExperiment?.value === adRevenueExperiment.disabled ||
             (this.adRevenueExperiment?.value !== adRevenueExperiment.variantC &&
             this.adRevenueExperiment?.value !== adRevenueExperiment.variantD);
    },
    showAdPillAtStart() {
      // since this is also the current state, we want to return true
      // when the experiment is not active at all
      return this.adRevenueExperiment?.value === adRevenueExperiment.disabled ||
             (this.adRevenueExperiment?.value !== adRevenueExperiment.variantB &&
             this.adRevenueExperiment?.value !== adRevenueExperiment.variantD);
    },
    showAdPillAtEnd() {
      return this.adRevenueExperiment?.value === adRevenueExperiment.variantB ||
             this.adRevenueExperiment?.value === adRevenueExperiment.variantD;
    },
  },
  created() {
    this.adRevenueExperiment = this.getFeatureToggle(adRevenueExperiment.flag);
  },
  methods: {
    getExtension(extType) {
      const tempExt = this.extensions.find((extension) => extension.type === extType);
      if (tempExt) {
        const { type, ...ext } = tempExt;
        return ext;
      }
      return null;
    },
    onTitleClick() {
      trackClick(this.trackingOptions);
      this.$emit('ad-click');
    },
    onAdReportClick() {
      this.$refs['ad-report']?.$el.click();
    },
  },
};
</script>

<style lang="scss" scoped>
.ad-result__description {
  margin: 0;
}

.ad-results__columns {
  display: flex;
}
</style>
